/**
 * Canonical public URLs for stock pages (`/s/<exchange>/<ticker>/<tab>`).
 *
 * The backend's `exchangesymbol` is not a stable URL key. The same Nasdaq stock
 * comes back as `Nasdaq` on one request and `NasdaqGS` on the next, and
 * `/API/v2/s/<exchange>/<ticker>/` does not resolve every exchange symbol it
 * hands out (in Oct 2026 `NasdaqGS/AAPL` and `NYSE/PLTR` answered 400 while
 * `US/<ticker>` answered for both). Pages built their canonical from the raw
 * symbol, so Google was told to index URLs that 404.
 *
 * Every alias of a US venue therefore maps to ONE exchange segment here, the
 * lookup goes through `US/<ticker>` (which resolves every US listing), and the
 * page 301s anything that is not exactly the canonical path. The canonical
 * segments match what the backend sitemap already emits (`NYSE`, `Nasdaq`,
 * `NYSEAM`, `OTCPK`), so sitemap URLs stay 200s.
 */

/**
 * Lower-cased alias -> canonical exchange segment, for US venues only.
 * Other exchanges keep the backend's symbol, whose case is fixed per exchange
 * (exchange symbols are unique case-insensitively in `screening_exchange`).
 */
const US_EXCHANGE_ALIASES: Record<string, string> = {
    nasdaq: "Nasdaq",
    nasdaqgs: "Nasdaq",
    nasdaqgm: "Nasdaq",
    nasdaqcm: "Nasdaq",
    xnas: "Nasdaq",
    xngs: "Nasdaq",
    xnms: "Nasdaq",
    xncm: "Nasdaq",
    nyse: "NYSE",
    xnys: "NYSE",
    nyseam: "NYSEAM",
    nysemkt: "NYSEAM",
    amex: "NYSEAM",
    xase: "NYSEAM",
    arca: "ARCA",
    nysearca: "ARCA",
    arcx: "ARCA",
    bats: "BATS",
    otcpk: "OTCPK",
};

/** The pseudo-exchange the backend resolves for any US listing. */
export const US_LOOKUP_EXCHANGE = "US";

/** The canonical URL segment for an exchange symbol, or the symbol itself when it is not a known US alias. */
export const canonicalExchange = (exchange: string): string => US_EXCHANGE_ALIASES[(exchange || "").toLowerCase()] || exchange;

/** True for a known US venue symbol or alias (case-insensitive), and for the `US` pseudo-exchange. */
export const isUsExchangeAlias = (exchange: string): boolean => {
    const lower = (exchange || "").toLowerCase();
    return lower === US_LOOKUP_EXCHANGE.toLowerCase() || lower in US_EXCHANGE_ALIASES;
};

/**
 * Exchanges to try, in order, when resolving `/s/<exchange>/<ticker>` against
 * `/API/v2/s/`. US aliases go through `US` first because it resolves every US
 * listing; the requested symbol is the fallback.
 */
export const stockLookupExchanges = (exchange: string): string[] => {
    if (!isUsExchangeAlias(exchange)) return [exchange];
    return exchange.toUpperCase() === US_LOOKUP_EXCHANGE ? [US_LOOKUP_EXCHANGE] : [US_LOOKUP_EXCHANGE, exchange];
};

interface StockRef {
    exchangesymbol: string;
    ticker: string;
}

/** `/s/<exchange>/<ticker>[/<tab>]` for a stock, with the exchange canonicalized. */
export const stockPath = (stock: StockRef, tab = ""): string => {
    const base = `/s/${encodeURIComponent(canonicalExchange(stock.exchangesymbol))}/${encodeURIComponent(stock.ticker)}`;
    return tab ? `${base}/${tab}` : base;
};

export const APP_ORIGIN = "https://app.ortex.com";

/** Absolute canonical URL for a stock page tab. */
export const stockCanonicalUrl = (stock: StockRef, tab = ""): string => `${APP_ORIGIN}${stockPath(stock, tab)}`;

/**
 * Middleware-side normalization of a `/s/...` pathname that needs no backend
 * call: known US exchange aliases go to their canonical segment, US tickers are
 * upper-cased, and the tab segments are lower-cased (every tab route is
 * lower-case). Returns the normalized pathname, or null when it is already
 * normal or is not a stock path. Non-US exchange/ticker case is left to the
 * page, which redirects once the backend has told it the real symbol.
 */
export const normalizeStockPathname = (pathname: string): string | null => {
    const parts = pathname.split("/");
    // ["", "s", exchange, ticker, ...tab]
    if (parts[1] !== "s" || parts.length < 4 || !parts[2] || !parts[3]) return null;
    const [, , exchange, ticker, ...tab] = parts;
    const us = isUsExchangeAlias(exchange) && exchange.toUpperCase() !== US_LOOKUP_EXCHANGE;
    const normalized = ["", "s", us ? canonicalExchange(exchange) : exchange, us ? ticker.toUpperCase() : ticker, ...tab.map((segment) => segment.toLowerCase())].join("/");
    return normalized === pathname ? null : normalized;
};
